import type { SubscriptionCadence } from "utils/contacts/recurringBilling";

/**
 * How a plan's cadence and money read. Shared by the Recurring billing panel
 * and the Subscription Details drawer, which have to agree: the panel's row
 * and the drawer it opens describe the same plan.
 *
 * Both maps hold translation KEYS, so a stored cadence never reaches a bare
 * `t()` (see client/AGENTS.md).
 *
 * Declared shortest cadence first: the Frequency picker renders them in
 * insertion order, and a list running Weekly to Annually is the one a person
 * can scan.
 */
export const CADENCE_LABEL_KEY: Record<SubscriptionCadence, string> = {
  WEEKLY: "Weekly",
  BIWEEKLY: "Bi-weekly",
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  ANNUALLY: "Annually",
};

export const CADENCE_SUFFIX_KEY: Record<SubscriptionCadence, string> = {
  WEEKLY: "PerWeekShort",
  BIWEEKLY: "PerTwoWeekShort",
  MONTHLY: "PerMonthShort",
  QUARTERLY: "PerQuarterShort",
  ANNUALLY: "PerYearShort",
};

/**
 * A plan's money, in the reader's locale.
 *
 * The locale is passed for the reason `shortDate` passes it: `undefined` here
 * followed the browser rather than i18next, so a Spanish reader on an en-US
 * browser got en-US separators. The currency is not a guess and stays USD.
 */
export const money = (cents: number, fractionDigits: number, locale: string) =>
  (cents / 100).toLocaleString(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

/**
 * A plan's dates, in the reader's locale.
 *
 * The locale is passed rather than read here, because i18next's active
 * language is the app's answer and the runtime default is the machine's.
 * The timezone is not: these are UTC instants, and rendering one in the
 * viewer's zone moves an invoice across a day boundary.
 */
export const shortDate = (epochMs: number, locale: string) =>
  new Date(epochMs).toLocaleDateString(locale, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
